import React from 'react';
import { fromPairs, map, find, isEmpty, isEqual } from 'lodash';
import classnames from 'classnames';

// local dependencies
import { withNetworkState } from 'ui/hoc/withNetworkState';
import { Form } from 'modules/form';
import { button as Button } from 'modules/button';
import { getLabel } from 'utils/getLabel';
import { inputField as InputField } from 'ui/views/generic/inputField';
import { widgetPreloader as WidgetPreloader } from 'ui/views/generic/widgetPreloader';

/**
 * @type { object }
 * @desc A React presentation/dumb view component.
 * @example
 * import { authFlowCallerIdentificationOthers as AuthFlowCallerIdentificationOthers } from 'ui/views/widgets/authFlowCallerIdentificationOthers';
 *
 * // render
 * <AuthFlowCallerIdentificationOthers propName='propValue'/>
 */

// caller detail section ( editable )
const CallerDetailSectionEditable = ( props ) => {
	const callerDetails = props.componentData.sections.callerDetails;
	const fieldsData = callerDetails.fields;

	// extracting actions
	const cancelAction = find( callerDetails.actions, { name: 'cancel' } );
	const saveAction = find( callerDetails.actions, { name: 'save' } );

	return (
		<div className='ui-auth-flow-caller-identification-others__caller-details'>
			<div className='ui-auth-flow-caller-identification-others__caller-details__title'>
				{ getLabel( callerDetails.title ) }
			</div>

			<Form
				fields={ fromPairs( map( fieldsData, ( field ) => [ field.name, field ] ) ) } // convert to object as specified by `Form` prop
				values={ props.othersInfo } // default values
				isValid={ false }
				mode={ props.mode }
				onSubmit={ ( { values, isValid } ) => {

					if( isValid ) {
						props.onFormSubmit( {
							title: values.title,
							firstName: values.firstName,
							lastName: values.lastName
						} );
					}
					
				} }
			>
				{ ( { fields, values, isValid, errors, onChange, onSumbit } = {} ) => {
					const classNameField = classnames( {
						'ui-auth-flow-caller-identification-others__caller-details__detailsForm__fields-inputEditable': !isEqual(
							props.mode,
							'readonly'
						),
						'ui-auth-flow-caller-identification-others__caller-details__detailsForm__fields-inputReadonly': isEqual(
							props.mode,
							'readonly'
						),
					} );

					return (
						<form
							onSubmit={ onSumbit }
							className='ui-auth-flow-caller-identification-others__caller-details__detailsForm'
						>
							{ /* fields */ }
							{ map( fields, ( field ) => {
								return (
									<field.view
										key={ field.name }
										className={ classNameField }
										name={ field.name }
										required={ field.required }
										placeholder={ field.placeholder }
										label={ field.title }
										onChange={ onChange }
										value={ values[ field.name ] }
										error={ errors[ field.name ] }
										mode={ props.mode }
									/>
								);
							} ) }

							{ /* submit/edit action */ }
							<Button
								id={ saveAction.name }
								disabled={ !isValid }
								type='submit'
								size='large'
								label={ saveAction.title }
								className='ui-auth-flow-caller-identification-others__caller-details__detailsForm__fields-button'
							/>

							{ /* cancel action */ }
							{ isEmpty( props.othersInfo ) ||
							isEqual( props.mode, 'readonly' ) ? null : (
								<Button
									id={ cancelAction.name }
									size='large'
									outline={ true }
									title={ cancelAction.title }
									className='ui-auth-flow-caller-identification-others__caller-details__detailsForm__fields-button'
									onClick={ () => props.onToggleEditMode( false ) }
								/>
							) }
						</form>
					);
				} }
			</Form>
		</div>
	);
};

// set display name
CallerDetailSectionEditable.displayName =
	'authFlowCallerIdentificationOthersSuccess.CallerDetailSectionEditable';

/* ------------------ */

// caller detail section ( readonly )
const CallerDetailSectionReadOnly = ( props ) => {
	const callerDetails = props.componentData.sections.callerDetails;
	const fieldsData = callerDetails.fields;

	// extracting actions
	const editAction = find( callerDetails.actions, { name: 'edit' } );

	return (
		<div className='ui-auth-flow-caller-identification-others__caller-details'>
			<div className='ui-auth-flow-caller-identification-others__caller-details__title'>
				{ getLabel( callerDetails.title ) }
			</div>

			<div className='ui-auth-flow-caller-identification-others__caller-details__readonly-view'>
				{ /* fields */ }
				{ map( fieldsData, ( field ) => {
					return (
						<InputField
							key={ field.name }
							className='ui-auth-flow-caller-identification-others__caller-details__readonly-view__field'
							name={ field.name }
							label={ field.title }
							value={ props.othersInfo[ field.name ] }
							mode='readonly'
						/>
					);
				} ) }

				{ /* edit action */ }
				<Button
					size='large'
					title={ editAction.title }
					onClick={ () => {
						props.onToggleEditMode( true );
					} }
				/>
			</div>
		</div>
	);
 };

// set display name
CallerDetailSectionReadOnly.displayName =
	'authFlowCallerIdentificationOthersSuccess.CallerDetailSectionReadOnly';

/* ------------------ */

export const authFlowCallerIdentificationOthersSuccess = ( props ) => {

	// dynamic class name
	const widgetClassName = classnames(
		'ui-auth-flow-caller-identification-others',
		props.className
	);

	return (
		<div className={ widgetClassName }>
			{ /* Caller Details form */ }
			{ isEqual( props.mode, 'readonly' ) ? (
				<CallerDetailSectionReadOnly { ...props } />
			) : (
				<CallerDetailSectionEditable { ...props } />
			) }
		</div>
	);
 };

// set display name
authFlowCallerIdentificationOthersSuccess.displayName =
	'authFlowCallerIdentificationOthersSuccess';

// set default props
authFlowCallerIdentificationOthersSuccess.defaultProps = {};

// prop types
authFlowCallerIdentificationOthersSuccess.propTypes = {};

/* ------------------ */

export const authFlowCallerIdentificationOthersPreloader = () => {
	return (
		<div className='ui-auth-flow-caller-identification-others-preloader'>
			<WidgetPreloader />
		</div>
	);
 };

// set display name
authFlowCallerIdentificationOthersPreloader.displayName =
	'authFlowCallerIdentificationOthersPreloader';

// set default props
authFlowCallerIdentificationOthersPreloader.defaultProps = {};

// prop types
authFlowCallerIdentificationOthersPreloader.propTypes = {};

/* ********************************************************* */

export const authFlowCallerIdentificationOthers = withNetworkState(
	authFlowCallerIdentificationOthersPreloader,
	authFlowCallerIdentificationOthersSuccess
 );
